import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Flex, Text, Spinner, Icon, Tooltip, Skeleton, Image, useColorMode,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../../utils/formatters';
import {
  formatDate, formatDateShort, formatDateTime, monthLong, dateToWords,
} from '../../utils/intlFormatters';
import { fetchGroupRows } from '../../api/endpointsBackend';
import { useDisplayCurrency } from '../../contexts/DisplayCurrencyContext';
import useGoalsEnabled from '../../hooks/useGoalsEnabled';
import { useDisplayAmount } from '../../contexts/ConvertedAmountsContext';
import { CategoryPill, getCategoryDisplay } from '../shared/InlineCategoryPicker';
import { categoryTint } from '../../utils/categoryTint';
import { isPrimaryTextRestricted } from '../../utils/movementDisplay';
import LoadMoreButton from './LoadMoreButton';
import GoalMark from '../goals/GoalMark';
import { GoalBar } from '../goals/GoalProgress';
import Card, { cardSurface } from '../shared/Card';
import RateSourceTooltip from '../shared/RateSourceTooltip';
import {
  VIEW_BOARD, GROUPING_CATEGORY, GROUPING_ACCOUNT, GROUPING_TYPE,
  GROUPING_CURRENCY, GROUPING_DAY, GROUPING_MONTH, GROUPING_YEAR, GROUPING_GOAL,
  rowDimensionFor,
} from '../../hooks/useDisplaySettings';

/**
 * MI-139 — the grouped Movements view (List and Board).
 *
 * Headers arrive as aggregates only. Each group's first window of rows is
 * prefetched in the background so expanding renders from state instead of
 * showing a spinner; "show more" stays lazy and collapsing evicts. Peak
 * memory is (groups × rows per group), independent of the period's size.
 */

/** Subgroups prefetch narrower than top-level groups: they multiply. */
const PREFETCH_SUBGROUPS = 4;

/** The server's ceiling for one row window (GroupRowsSchema.limit). A day
 *  bigger than this keeps its "show more" — which is the right answer for the
 *  one day in a workspace that holds 500 movements. */
const MAX_ROW_WINDOW = 500;

/**
 * When a group has to read its rows again, as the page knows it.
 *
 * `generation` moves when the whole view was reloaded: every group starts over.
 * `versions` moves per group key when an edit changed that group's numbers: only
 * that group reads again, with the rows on screen kept until the new ones land.
 * A context rather than props because a group can sit under a parent, a board
 * column or a swimlane, and each of them would have to hand both down.
 */
const GroupRefreshContext = createContext({ generation: 0, versions: {} });

/**
 * Placeholder rows for a group whose window is still in flight.
 *
 * The prefetch is deliberately silent — no spinner while it works in the
 * background — but an *open* group showing nothing reads as empty rather than
 * as loading. These stand in at roughly the real row's height, so a group
 * filling in doesn't shove the rest of the list down.
 */
const SkeletonRows = ({ count, asCard = false, depth = 0 }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <Flex
        key={i}
        align="center"
        gap={2.5}
        px={2.5}
        py={asCard ? 2.5 : 1.5}
        pl={asCard ? 2.5 : 8 + depth * 4}
        // Later rows fade out: the eye reads it as "more is coming" rather
        // than as a block of broken content.
        opacity={1 - i * (0.6 / Math.max(count, 1))}
      >
        <Skeleton h="14px" w="14px" borderRadius="sm" flex="none" />
        <Skeleton h="12px" w={`${38 + ((i * 13) % 26)}%`} borderRadius="sm" />
        <Box flex={1} />
        <Skeleton h="12px" w="72px" borderRadius="sm" flex="none" />
      </Flex>
    ))}
  </>
);

/**
 * What identifies a category or account group at a glance (MI-139 rev 6).
 *
 * A column of uppercase labels reads flat; the icon is the thing the eye
 * actually lands on. Accounts prefer their institution's logo — the same rule
 * `AccountRow` follows on the Accounts page — and fall back to their own emoji
 * when there is no source, which is what cash and person-to-person accounts
 * rely on. Categories carry an emoji and a colour instead.
 *
 * `meta` is resolved by the page, which already holds the category and account
 * lists; the aggregate rows carry only ids and names.
 */
const GroupIcon = ({ meta, size = '15px' }) => {
  if (!meta) return null;
  // A goal brings its own disc rather than an emoji or a bank logo.
  if (meta.node) return meta.node;
  if (meta.logo) {
    return (
      <Image
        src={meta.logo}
        alt=""
        boxSize={size}
        objectFit="contain"
        borderRadius="sm"
        flex="none"
        fallback={<Text fontSize="xs" lineHeight="1" flex="none">{meta.icon || '🏦'}</Text>}
      />
    );
  }
  if (!meta.icon) return null;
  return <Text fontSize="xs" lineHeight="1" flex="none">{meta.icon}</Text>;
};

const ChevronIcon = ({ open }) => (
  <Icon viewBox="0 0 16 16" boxSize={3} color="redesign.text.muted" flex="none"
        transform={open ? 'rotate(90deg)' : undefined} transition="transform 0.12s">
    <path d="M6 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.6"
          strokeLinecap="round" strokeLinejoin="round" />
  </Icon>
);

/** Shown where a total would go when a group spans currencies — summing them
 *  would produce a number that means nothing. */
const MultiCurrency = ({ currencies }) => {
  const { t } = useTranslation('movements');
  const label = t('display.group.multiCurrency', { count: currencies.length });
  return (
    <Tooltip label={currencies.join(' · ')} openDelay={300} hasArrow>
      <Flex align="center" gap={1} color="redesign.text.muted" flex="none">
        <Icon viewBox="0 0 16 16" boxSize={3} fill="none" stroke="currentColor" aria-hidden>
          <path d="M2.5 5.5h11M11 3l2.5 2.5L11 8M13.5 10.5h-11M5 8l-2.5 2.5L5 13"
                strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </Icon>
        <Text fontSize="xs" whiteSpace="nowrap">{label}</Text>
      </Flex>
    </Tooltip>
  );
};

function useGroupLabel() {
  const { t } = useTranslation('movements');
  return useCallback((group) => {
    const { grouping, value, label, year } = group;
    switch (grouping) {
      case GROUPING_CATEGORY:
      case GROUPING_ACCOUNT:
        return label || t('display.group.uncategorised');
      case GROUPING_GOAL:
        return label || t('goals:list.noGoal');
      case GROUPING_TYPE:
        // The backend stores "Income"/"Expenses"/"Transfer"; the i18n keys are
        // lowercase, so a raw interpolation misses every one of them.
        return value
          ? t(`types.${String(value).toLowerCase()}`, { defaultValue: value })
          : '—';
      case GROUPING_CURRENCY:
        return value || '—';
      case GROUPING_DAY:
        // The same words the ungrouped list uses. Grouping by day is that
        // list, collapsed — it should not look like a different feature.
        return value ? dateToWords(String(value), { alwaysShowYear: true }) : '—';
      case GROUPING_MONTH:
        return value ? `${monthLong(Number(value) - 1)} ${year}` : '—';
      case GROUPING_YEAR:
        return String(value ?? '—');
      default:
        return '';
    }
  }, [t]);
}

/** A group's scope for the row-window endpoint, walking parent → child. */
function scopesFor(group, parent) {
  const scope = (g) => ({ dimension: rowDimensionFor(g.grouping), value: g.value });
  return parent ? [scope(parent), scope(group)] : [scope(group)];
}

/** One movement. Columns follow the Display properties; board columns are
 *  264px, where a horizontal row crushes the name and clips the amount, so a
 *  card stacks the same fields instead. */
const GroupRow = ({ movement, isVisible, onSelect, asCard = false, depth = 0 }) => {
  const { t } = useTranslation('movements');
  const goalsEnabled = useGoalsEnabled();
  // A row that no longer counts keeps its mark whatever the menu says: the
  // goal header above it cannot report that — but nothing shows while Goals
  // is off in the workspace.
  const showGoal = Boolean(movement.goal) && goalsEnabled &&
    (isVisible('goal') || movement.goal.effect === 'invalid');
  // The count is on every movement shape; the array is not something a row
  // should have to hold just to learn whether it holds anything.
  const reconciled = (movement.num_transactions || 0) > 0;
  // MI-318 — the board renders its own card instead of reusing the list row, so
  // it needs the converted value explicitly; without this it kept showing the
  // original currency while the group header above it read ≈ COP.
  const shown = useDisplayAmount(movement);
  // Same rule as CopilotAmount on the movements list: a transfer is neither
  // income nor spend, so it reads neutral rather than green.
  const amountColor = movement.type === 'Income' ? 'redesign.amount.income'
    : movement.type === 'Expenses' ? 'redesign.amount.expense'
    : 'redesign.amount.neutral';
  const accountName = movement.account?.name || movement.from_account?.name || '';
  // Same distinction the flat list draws: a role that may not read the note
  // gets no note field at all, with the has_* flag still set, and printing the
  // empty fallback there would say "no note" about a movement that has one.
  const title = isPrimaryTextRestricted(movement)
    ? t('list.fallback.restricted')
    : (movement.note || movement.description || '—');
  // The same two the list row carries, drawn read-only: the card is a summary,
  // and a 264px column has no room for the pickers' hit areas.
  const tags = (movement.tag_movements || []).map(tm => tm.tag).filter(Boolean);
  // Null for a transfer, which has no category by definition.
  const categoryLabel = getCategoryDisplay(movement);
  const showCategory = isVisible('category') && Boolean(categoryLabel);

  const check = (
    <Icon viewBox="0 0 16 16" boxSize={3} color="redesign.accent.blue" aria-hidden flex="none">
      <circle cx="8" cy="8" r="7" fill="currentColor" />
      <path d="M5 8.2l2 2 4-4.2" fill="none" stroke="white" strokeWidth="1.6"
            strokeLinecap="round" strokeLinejoin="round" />
    </Icon>
  );

  if (asCard) {
    return (
      <Card
        // 8px of padding rather than 10: two lines of title need 33px and the
        // card had 29 to give, so a long name was squeezed to a line and a
        // half and cut through the middle of its own letters. The four pixels
        // come from here, and the card keeps the height it had.
        borderRadius="md" px={3} py={2} cursor="pointer"
        h={CARD_H} display="flex" flexDirection="column" overflow="hidden"
        _hover={{ borderColor: 'redesign.text.faint' }}
        // Dimmed and struck through like a list row (MI-315): the column
        // header above already leaves an ignored movement out of its total.
        opacity={movement.is_ignored ? 0.55 : 1}
        onClick={() => onSelect?.(movement.id)}
      >
        <Text
          fontSize="xs" fontWeight="600" color="redesign.text"
          noOfLines={2} lineHeight="1.35"
          textDecoration={movement.is_ignored ? 'line-through' : 'none'}
          // Never squeezed: two lines or one, always whole ones. If something
          // else ever outgrows the card, it clips at the edge instead of
          // slicing a line of text in half.
          flexShrink={0}
        >
          {title}
        </Text>
        {/* One line for everything that qualifies the movement — account, its
            reconciled tick, tags, category. Stacking them cost the card a third
            of its height for information that fits beside itself.
            The account sits left and the chips are pushed right, so the
            categories line up down the column the way the amounts below them
            do: dragged along by the account name, each chip started at a
            different x and the column read as noise. Category goes last, hard
            against the padding, because it is the one that has to align — a
            tag badge in between would knock it out of the column on exactly
            the cards that carry one. The account is also what gives way when
            space runs out: it is the one of the four that repeats down a
            column. */}
        {((isVisible('account') && accountName) || (isVisible('reconciled') && reconciled)
          || showCategory || tags.length > 0 || showGoal) && (
          <Flex align="center" gap={1.5} mt={1.5} minW={0}>
            {isVisible('account') && accountName && (
              <Text
                fontSize="xs" color="redesign.text.muted" noOfLines={1}
                minW={0} flexShrink={1}
              >
                {accountName}
              </Text>
            )}
            {isVisible('reconciled') && reconciled && check}
            <Box flex={1} minW="4px" />
            {showGoal && <GoalMark movement={movement} size={15} />}
            {tags.length > 0 && (
              // Same dots-and-count badge the list row uses, so a tagged
              // movement reads the same in both views.
              <Flex {...cardSurface}
                align="center" gap="3px"
                borderRadius="full" px="6px" h="17px" flexShrink={0}
              >
                {tags.slice(0, 3).map(tag => (
                  <Box key={tag.id} w="7px" h="7px" borderRadius="full"
                       bg={tag.color || '#3182CE'} />
                ))}
                <Text fontSize="2xs" fontWeight="500" color="redesign.text" ml="2px">
                  {tags.length}
                </Text>
              </Flex>
            )}
            {showCategory && (
              <CategoryPill movement={movement} maxW="65%" h="20px" fontSize="xs" />
            )}
          </Flex>
        )}

        {/* Pinned to the bottom of the card, so the amounts line up across a
            row no matter how long the titles above them run. */}
        <Flex align="baseline" justify="space-between" gap={2} mt="auto" pt={1.5}>
          {/* The movement's date is the card's own; `created` joins it under
              the Display toggle, exactly as on a list row. */}
          <Flex align="baseline" gap={1.5} minW={0}>
            {isVisible('date') && movement.date && (
              <Text fontSize="2xs" color="redesign.text.faint" whiteSpace="nowrap">
                {formatDate(movement.date)}
              </Text>
            )}
            {isVisible('created') && movement.created_at && (
              <Tooltip
                label={t('list.row.createdTooltip', {
                  date: formatDateTime(movement.created_at),
                })}
                openDelay={300} hasArrow fontSize="xs" placement="top"
              >
                <Text fontSize="2xs" color="redesign.text.faint" whiteSpace="nowrap">
                  {formatDateShort(movement.created_at)}
                </Text>
              </Tooltip>
            )}
          </Flex>
          <RateSourceTooltip sources={shown.rateSources} isDisabled={!shown.isConverted}>
          <Flex
            align="baseline"
            gap={1}
            ml="auto"
            /* Same rule as the list row: dim until the converted value lands. */
            opacity={shown.isPending ? 0.35 : 1}
            transition="opacity 0.15s ease-out"
          >
            {shown.isConverted && (
              <Text fontSize="2xs" fontWeight="600" color="redesign.text.muted">≈</Text>
            )}
            {isVisible('currency') && (
              <Text fontSize="2xs" fontWeight="600" letterSpacing="0.05em" color="redesign.text.muted">
                {shown.currency}
              </Text>
            )}
            <Text
              textStyle="tabularNumbers"
              fontSize="xs" fontWeight="700" whiteSpace="nowrap" color={amountColor}
              textDecoration={movement.is_ignored ? 'line-through' : 'none'}
            >
              {formatMoney(shown.amount, shown.currency, true)}
            </Text>
          </Flex>
          </RateSourceTooltip>
        </Flex>
      </Card>
    );
  }

  return null;
};

/** The group's headline figure, or the multi-currency indicator when the
 *  group spans currencies and no single number can express it. */
/** True when this level, or the level above it, pins the type to Transfer. */
function isTransferScope(group, parent) {
  const pins = (g) => g?.grouping === GROUPING_TYPE && g.value === 'Transfer';
  return pins(group) || pins(parent);
}

const GoalTotal = ({ summary, color, size = 'sm' }) => (
  <Flex align="center" gap={1.5} flex="none">
    <Box w="56px" flex="none" display={{ base: 'none', md: 'block' }}>
      <GoalBar pct={summary.pct} color={color} h="4px" />
    </Box>
    <Text fontSize="xs" color="redesign.text.muted">{Math.round(summary.pct)}%</Text>
    <Text textStyle="tabularNumbers" fontSize={size} fontWeight="600" color="redesign.heading">
      <Text as="span" fontSize="2xs" color="redesign.text.muted" mr={1}>{summary.currency}</Text>
      {summary.saved.toLocaleString('en-US', { maximumFractionDigits: 0 })}
    </Text>
    <Text textStyle="tabularNumbers" fontSize="xs" color="redesign.text.muted">
      / {summary.target.toLocaleString('en-US', { maximumFractionDigits: 0 })}
    </Text>
  </Flex>
);

const GroupFigure = ({ group, parent, meta, size = 'sm' }) => (
  meta?.summary
    ? <GoalTotal summary={meta.summary} color={meta.barColor} size={size} />
    : <GroupTotal group={group} parent={parent} size={size} />
);

const GroupTotal = ({ group, parent, size = 'sm' }) => {
  // A synthesized empty bucket spans no currencies, so there is no unit to
  // borrow and no multi-currency indicator to show — just the zero (rev 5).
  if (group.isEmpty && !group.currency) {
    return (
      <Text
        textStyle="tabularNumbers"
        fontSize={size} fontWeight="600" color="redesign.text.faint" flex="none"
      >
        0
      </Text>
    );
  }
  if (group.currency === null) return <MultiCurrency currencies={group.currencies} />;

  // A Transfer nets to zero by definition, so the net total is the wrong
  // headline for a transfer group — it would always read 0.00. Show what
  // actually moved, and colour it the way the movements list colours a
  // transfer: neutral, because it is neither income nor spend. The currency
  // subgroup of a Transfer group inherits this, hence the parent check.
  const isTransfer = isTransferScope(group, parent);
  const total = isTransfer ? (group.totalAmount ?? 0) : (group.total ?? 0);
  const color = isTransfer ? 'redesign.amount.neutral'
    : total < 0 ? 'redesign.amount.expense'
    : total > 0 ? 'redesign.amount.income'
    : 'redesign.amount.neutral';

  return (
    <RateSourceTooltip sources={group.rateSources} isDisabled={!group.isConverted}>
    <Flex align="baseline" gap={1} flex="none">
      {/* MI-318: a converted headline is approximate, and says so with the same
          `≈` the rows use — a group that spans currencies has a total at all
          only because it was converted. */}
      {group.isConverted && (
        <Text fontSize="2xs" fontWeight="600" color="redesign.text.muted">≈</Text>
      )}
      <Text fontSize="2xs" fontWeight="600" letterSpacing="0.05em" color="redesign.text.muted">
        {group.currency}
      </Text>
      <Text
        textStyle="tabularNumbers"
        fontSize={size} fontWeight="600" whiteSpace="nowrap" color={color}
      >
        {formatMoney(total, group.currency, true)}
      </Text>
    </Flex>
    </RateSourceTooltip>
  );
};

/**
 * One group. Owns its own row window and cursor, so one group's "show more"
 * never disturbs another's position. A group with children renders those
 * instead of rows — rows live at the deepest level only.
 */
const Group = ({
  group, parent, ordering, rowsPerGroup, isVisible, filters, filterGroups,
  groupsOperator, startDate, endDate, onSelectMovement,
  renderRows, onRowsLoaded, resolveRows, groupMeta,
  asCard = false, depth = 0, prefetch = true, cell = false, defaultOpen,
}) => {
  const { t } = useTranslation('movements');
  const { colorMode } = useColorMode();
  const label = useGroupLabel()(group);
  const meta = groupMeta?.(group);
  const hasChildren = Array.isArray(group.children) && group.children.length > 0;
  // A coloured group (a category) is tinted in its own hue, at a strength
  // that reads in both modes; the rest sit on the sunken band.
  const tint = meta?.color ? categoryTint(meta.color, colorMode).bg : null;

  // Grouping by day is the ungrouped list, collapsed only if you ask for it —
  // so a day arrives expanded. Every other grouping starts closed: the header
  // is the point there, and opening all of them would defeat it.
  const { displayCurrency } = useDisplayCurrency();
  // A swimlane cell has no header of its own — the column above it and the
  // lane beside it carry the labels — so it is always open and the lane is
  // what collapses (rev 5). `defaultOpen` lets the board open its columns on
  // arrival: a board of closed columns is a row of headers and no board.
  const [open, setOpen] = useState(() =>
    cell || defaultOpen || (group.grouping === GROUPING_DAY && !asCard)
  );
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [error, setError] = useState(false);
  // A quiet prefetch leaves `loading` false on purpose, so a click landing
  // while it is in flight used to start a second, spinner-showing fetch of the
  // same window. This tracks both kinds.
  const inFlight = useRef(false);
  // The newest read of this group's first window. An answer that is no longer
  // the newest describes rows from before an edit, and is dropped.
  const firstWindowRead = useRef(0);
  const { generation, versions } = useContext(GroupRefreshContext);
  // A subgroup, or a board cell, follows its parent: the aggregates its rows
  // come from are read per parent.
  const version = versions[(parent || group).key] ?? 0;

  const count = group.count ?? 0;
  const remaining = Math.max(count - rows.length, 0);

  /**
   * How many rows one window asks for.
   *
   * MI-336 — a day group takes all of them. Day is the default view now, and
   * the flat list it replaces showed every movement of a day; "rows per group"
   * is a windowing rule for the groups you go looking for, and the view the
   * page opens on is not one of those. A board card keeps the window: 300
   * cards in a 264px column is not a column.
   */
  const isFullDay = group.grouping === GROUPING_DAY && !asCard;
  const windowSize = isFullDay
    ? Math.min(Math.max(count, rowsPerGroup), MAX_ROW_WINDOW)
    : rowsPerGroup;

  // Which rows a group holds is fixed by its own value and its parent's. The
  // `group` object says no more than that, yet it is rebuilt every time the
  // list is — a further page of groups is enough — so keying the fetch on it
  // emptied every open group and read all of them again.
  const { grouping: groupGrouping, value: groupValue, year: groupYear } = group;
  const parentGrouping = parent?.grouping;
  const parentValue = parent?.value;
  const parentYear = parent?.year;
  const scopes = useMemo(() => scopesFor(
    { grouping: groupGrouping, value: groupValue },
    parentGrouping ? { grouping: parentGrouping, value: parentValue } : null,
  ), [groupGrouping, groupValue, parentGrouping, parentValue]);
  const year = groupGrouping === GROUPING_MONTH ? groupYear
    : parentGrouping === GROUPING_MONTH ? parentYear : null;

  // `force` reads again even with a read in flight, and `atLeast` keeps the
  // rows the reader had already opened: both are for an edit, which must not
  // be answered with what was true before it.
  const loadWindow = useCallback(async (nextCursor, { quiet = false, force = false, atLeast = 0 } = {}) => {
    if (inFlight.current && !nextCursor && !force) return false;
    inFlight.current = true;
    const read = nextCursor ? null : (firstWindowRead.current += 1);
    if (!quiet) setLoading(true);
    setError(false);
    try {
      const data = await fetchGroupRows({
        filters, groups: filterGroups, groupsOperator,
        scopes, year,
        orderBy: ordering,
        limit: Math.min(Math.max(windowSize, atLeast), MAX_ROW_WINDOW),
        cursor: nextCursor,
        start_date: startDate,
        end_date: endDate,
        // MI-318 — so the server ranks by converted value. Sending it only
        // when converting keeps the unconverted path byte-identical.
        displayCurrency,
      });
      if (read !== null && read !== firstWindowRead.current) return false;
      setRows(prev => (nextCursor ? [...prev, ...data.items] : data.items));
      // Hand the window to the page's row store, so the merge modal, the bulk
      // actions and the detail panel can see rows that live inside a group.
      onRowsLoaded?.(data.items);
      setCursor(data.next);
      setHydrated(true);
      return true;
    } catch {
      if (!quiet) setError(true);
      return false;
    } finally {
      if (read === null || read === firstWindowRead.current) inFlight.current = false;
      if (!quiet) setLoading(false);
    }
    // `displayCurrency` belongs here: switching it changes the *order* the
    // server returns, so a cached window would be stale rather than merely
    // relabelled. The effect below clears the rows for the same reason.
  }, [filters, filterGroups, groupsOperator, scopes, year, ordering,
      windowSize, startDate, endDate, displayCurrency, onRowsLoaded]);

  // Prefetch the first window so expanding renders from state. Fire-and-forget:
  // it never blocks the header, and a failure just falls back to fetching on
  // expand. Groups with children have no rows of their own to prefetch.
  useEffect(() => {
    setRows([]);
    setCursor(null);
    setHydrated(false);
    if (hasChildren || group.isEmpty) return;
    // Day groups render expanded, and past the prefetch budget nobody would
    // ever click them open — they would sit open and empty forever.
    if (!prefetch && !open) return;
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await loadWindow(null, { quiet: true });
    })();
    return () => { cancelled = true; };
  }, [loadWindow, prefetch, hasChildren, open, group.isEmpty, generation]);

  // An edit changed this group's numbers — a movement came in, left, or its
  // amount moved — so its rows are read again where they stand. The rows on
  // screen stay until the new ones land: swapping them for skeletons on every
  // save is the reload this exists to avoid. Collapsed, there is nothing on
  // screen to keep, so the window is dropped and expanding fetches it.
  const seenVersion = useRef(version);
  useEffect(() => {
    if (version === seenVersion.current) return;
    seenVersion.current = version;
    if (hasChildren || group.isEmpty) return;
    if (!open) {
      setRows([]);
      setCursor(null);
      setHydrated(false);
      return;
    }
    loadWindow(null, { quiet: true, force: true, atLeast: rows.length });
    // Only a new version acts: re-running on every render of the group would
    // read its rows over and over.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  const isEmpty = Boolean(group.isEmpty);

  const toggle = () => {
    // Nothing behind a synthesized bucket, so nothing to expand. The header
    // draws no chevron either — offering the affordance would be a lie.
    if (isEmpty) return;
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    // Normally the prefetch already filled this in; only a failed or skipped
    // prefetch reaches the network here.
    if (!hasChildren && rows.length === 0 && !inFlight.current) loadWindow(null);
  };

  const header = asCard ? (
    <Box
      px={3} pt={3} pb={2.5} cursor="pointer" borderBottom="1px solid"
      borderColor="redesign.border"
      // The whole header block carries the category's colour, not just the
      // name inside it: a pill that small was a detail nobody saw, while the
      // block gives the column a top the eye can find while scrolling
      // sideways. Washed down to 15% — a solid fill would drown the count and
      // the total sitting on it, and would be unreadable for the lighter
      // categories. Accounts have no colour of their own, so they keep the
      // plain header.
      bg={tint || undefined}
      _hover={{ bg: tint ? undefined : 'redesign.bg.hover', filter: tint ? 'brightness(0.97)' : undefined }}
      onClick={toggle} role="button" aria-expanded={open}
    >
      <Flex align="center" gap={2} mb={2}>
        <GroupIcon meta={meta} />
        {/* The name stays in a plain pill: it is what has to be read, and it
            reads best sitting on the tinted block rather than repeating it. */}
        <Text
          fontSize="xs" fontWeight="700" letterSpacing="0.03em" textTransform="uppercase"
          px={2} py={0.5} borderRadius="full" bg="redesign.bg.surface"
          color="redesign.text"
          noOfLines={1} minW={0}
        >
          {label}
        </Text>
        <Text fontSize="xs" color="redesign.text.muted" whiteSpace="nowrap" flex="none">{count}</Text>
        <Box flex={1} />
        <ChevronIcon open={open} />
      </Flex>
      <Flex align="baseline" gap={1.5}>
        <GroupFigure group={group} parent={parent} meta={meta} size="sm" />
        <Box flex={1} />
      </Flex>
    </Box>
  ) : (
    <Flex
      position="relative"
      // -1px, so two bands in a row share one hairline instead of drawing two.
      align="center" gap={2.5} px={3} py={0} minH="34px" mt="-1px"
      // The caret is positioned absolutely at 1px and is 12px wide, so content
      // has to start past 13px or it sits under the caret — which is what put
      // the arrow on the label's first letter and, one level down, on the
      // category icon. A subgroup does not step right: the coloured band and
      // the icon already say it is a child, and the indent only cost the label
      // room it needed at narrow widths.
      pl="20px"
      cursor={isEmpty ? 'default' : 'pointer'}
      // Dimmed, so a run of empty months reads as background against the
      // periods that actually hold something.
      opacity={isEmpty ? 0.5 : 1}
      // Each group is a band across the list, as Linear draws a status
      // group: its own tint for a category, the sunken ground otherwise,
      // between hairlines. Applies at either level, so a category subgroup is
      // tinted too.
      bg={tint || 'redesign.bg.sunken'}
      borderTop="1px solid"
      borderBottom="1px solid"
      borderColor="redesign.border"
      _hover={isEmpty ? undefined
        : tint ? { filter: 'brightness(0.97)' } : { bg: 'redesign.bg.hover' }}
      onClick={isEmpty ? undefined : toggle}
      role={isEmpty ? undefined : 'button'}
      aria-expanded={isEmpty ? undefined : open}
    >
      {/* Out of flow, in the gutter — same as the ungrouped list's date
          headers, so the label lines up with the row checkboxes below it
          instead of being pushed right by the chevron. */}
      {!isEmpty && (
        <Flex
          position="absolute"
          left="1px"
          top={0}
          bottom={0}
          align="center"
        >
          <ChevronIcon open={open} />
        </Flex>
      )}
      {/* Every grouping wears the ungrouped list's header, not just Day. A
          chip for categories and plain text for days made one feature look
          like two. */}
      <GroupIcon meta={meta} />
      <Text
        fontSize="2xs"
        fontWeight="700"
        letterSpacing="0.06em"
        textTransform="uppercase"
        // One colour for every label. Only some categories carry a colour, so
        // tinting the text made a handful of rows jump out of a list where the
        // highlight meant nothing — the icon already identifies the group.
        color="redesign.heading"
        noOfLines={1}
      >
        {label}
      </Text>
      <Text fontSize="xs" color="redesign.text.muted" whiteSpace="nowrap">
        {isEmpty ? t('display.group.emptyGroup') : count}
      </Text>
      <Box flex={1} />
      <GroupFigure group={group} parent={parent} meta={meta} />
    </Flex>
  );

  // Rendered even while collapsed — a child that has not mounted cannot
  // prefetch, which is what made opening a subgroup spin.
  const childList = hasChildren && (
    <Box pb={1} display={open ? undefined : 'none'}>
      {group.children.map((child, idx) => (
        <Group
          key={child.key}
          group={child}
          parent={group}
          ordering={ordering}
          rowsPerGroup={rowsPerGroup}
          isVisible={isVisible}
          filters={filters}
          filterGroups={filterGroups}
          groupsOperator={groupsOperator}
          startDate={startDate}
          endDate={endDate}
          onSelectMovement={onSelectMovement}
          renderRows={renderRows}
          onRowsLoaded={onRowsLoaded}
          resolveRows={resolveRows}
          groupMeta={groupMeta}
          asCard={false}
          depth={depth + 1}
          // Only the first few subgroups of an already-prefetched parent; a
          // parent × children fan-out is how the stampede got started.
          prefetch={prefetch && idx < PREFETCH_SUBGROUPS}
        />
      ))}
    </Box>
  );

  // Rendered from the store rather than from the fetched objects, so an edit
  // anywhere on the page reaches this window without it refetching.
  const visibleRows = resolveRows ? resolveRows(rows) : rows;

  const rowList = !hasChildren && !isEmpty && open && (
    <Box pb={cursor ? 1 : 0}
         px={asCard && !cell ? 2.5 : 0}
         pt={asCard && !cell ? 2.5 : 0}
         display={asCard ? 'flex' : undefined}
         flexDirection={asCard ? 'column' : undefined}
         gap={asCard ? 2 : undefined}>
      {asCard
        ? visibleRows.map(m => (
            <GroupRow key={m.id} movement={m} isVisible={isVisible}
                      onSelect={onSelectMovement} asCard depth={depth} />
          ))
        // The list view hands rows to the same component the ungrouped
        // Movements page uses, so a grouped row keeps every interaction it has
        // there — selection, inline category, tags, alerts, the lot.
        : renderRows?.(visibleRows)}

      {/* Open but not yet filled — the body only renders when open, so this is
          always a visible wait, whether the fetch was quiet or not. */}
      {!hydrated && !error && (
        <SkeletonRows
          count={Math.max(1, Math.min(windowSize, count))}
          asCard={asCard}
          depth={depth}
        />
      )}

      {/* Appending a further window: the rows above stay, so a small spinner
          under them is enough. */}
      {loading && hydrated && (
        <Flex align="center" gap={2} pl={asCard ? 0 : 8} py={2}>
          <Spinner size="xs" color="redesign.text.muted" />
        </Flex>
      )}

      {error && (
        <Text fontSize="xs" color="redesign.accent.red" pl={asCard ? 0 : 8} py={2}>
          {t('display.group.loadError')}
        </Text>
      )}

      {hydrated && !error && rows.length === 0 && (
        <Text fontSize="xs" color="redesign.text.muted" pl={asCard ? 0 : 8} py={2}>
          {t('display.group.empty')}
        </Text>
      )}

      {!loading && hydrated && rows.length > 0 && cursor && (
        // Left-aligned with the rows it belongs to, the way "load more inside a
        // section" reads everywhere else. Centred across the full width made it
        // look like a page-level action floating between groups.
        <Flex pl={asCard ? 0 : 8} pr={asCard ? 0 : 2.5} pb={1} pt={0.5}>
          <Flex
            as="button"
            align="center"
            gap={1}
            px={1.5}
            py={0.5}
            ml={-1.5}
            borderRadius="md"
            // 2xs, the same step as the group header's label — the smallest the
            // type scale offers, and enough to read as secondary.
            fontSize="2xs"
            fontWeight="500"
            color="redesign.text.muted"
            _hover={{ bg: 'redesign.bg.hover', color: 'redesign.text' }}
            transition="background 0.12s, color 0.12s"
            onClick={(e) => { e.stopPropagation(); loadWindow(cursor); }}
          >
            {/* Sized on the Text itself: Chakra's Text carries its own default
                size and does not inherit from the button around it. */}
            <Text fontSize="2xs">
              {t('display.group.showMore', { count: Math.min(windowSize, remaining) })}
            </Text>
            <Text fontSize="2xs" color="redesign.text.faint" fontWeight="400">
              {t('display.group.remaining', { count: remaining })}
            </Text>
          </Flex>
        </Flex>
      )}
    </Box>
  );

  // A swimlane cell is the intersection of a column and a band: both labels
  // are already on screen, so it draws rows and nothing else (rev 5).
  if (cell) {
    return <Box>{rowList}</Box>;
  }

  if (asCard) {
    return (
      <Box
        minW="264px" maxW="264px" flex="none" display="flex" flexDirection="column"
        alignSelf="flex-start" maxH="100%"
        bg="redesign.bg.card" border="1px solid" borderColor="redesign.border"
        borderRadius="lg" overflow="hidden"
      >
        {header}
        {open && (
          <Box overflowY="auto" overflowX="hidden">{childList}{rowList}</Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      // The ungrouped list's rhythm, for every grouping: breathing room instead
      // of a rule between groups.
      mb={depth ? 1 : 3}
      // A subgroup reads as a child of its parent, not a sibling: indent it and
      // hang a rule off the parent so the nesting is visible at a glance.
      borderLeft={depth ? '2px solid' : undefined}
      borderLeftColor={depth ? 'redesign.border' : undefined}
      ml={depth ? 6 : 0}
    >
      {header}
      {childList}
      {rowList}
    </Box>
  );
};

/** Board column width — the card layout is designed around it. */
const COLUMN_W = 264;

/**
 * Every board card is this tall, whatever its content.
 *
 * A two-line title made one card taller than its neighbours, so a column's
 * cards stopped lining up with the column beside it and every column ended at
 * a different height. Fixing the height buys a grid the eye can scan across;
 * the amount sits at the bottom of the card, so short titles leave the gap
 * above it rather than shifting the figure up.
 */
// Six pixels more than the 96 it used to be: that is what a two-line title
// needs once its lines are whole. Anything less and the card clipped its own
// bottom row instead.
const CARD_H = '102px';

/** Lanes and columns multiply, so the prefetch budget is a rectangle. */
const PREFETCH_LANES = 3;
const PREFETCH_LANE_COLUMNS = 5;

/**
 * One horizontal band: the same subgroup value seen across every column.
 *
 * This is the arrangement Linear uses, and the reason it beats nesting inside
 * a column — a `COP` band lets you read December against November against
 * October, which a subgroup trapped in its own column can never show.
 */
const Swimlane = ({ lane, columns, laneIndex, cellProps, groupMeta }) => {
  const label = useGroupLabel()(lane);
  const meta = groupMeta?.(lane);
  // Closed on arrival, exactly like a group in the list: the header is the
  // point of a grouping, and opening every band by default buries it.
  const [open, setOpen] = useState(false);

  return (
    <Box mb={2}>
      {/* The band itself, spanning every column — one bar the eye can follow
          from the leftmost card to the rightmost. A step darker than the page
          rather than the barely-there card grey, and close to the cards under
          it: the band and its contents should read as one block, not as two
          separated by a corridor of white. */}
      <Flex
        align="center" h="30px" mb={1.5}
        // A subgroup band is a row on the board, so it takes the colour the
        // same way a list row does — and at the column header's strength,
        // being a band of much the same weight.
        bg={meta?.color ? `${meta.color}26` : 'redesign.bg.bubble'}
        borderRadius="md"
        cursor="pointer"
        _hover={{ bg: meta?.color ? `${meta.color}33` : 'redesign.bg.hover' }}
        onClick={() => setOpen(o => !o)}
        role="button" aria-expanded={open}
      >
        {/* Pinned to the left edge: scrolling out to a far column must not
            take the band's own label off screen with it.

            No background of its own. It used to inherit the band's, which was
            invisible while that was an opaque grey and became a seam the moment
            it turned into a category colour: an alpha painted twice is a darker
            alpha, so the label sat in a deeper block than the band around it.
            Nothing scrolls underneath — the band holds only this — so there is
            nothing for a background to hide. */}
        <Flex
          align="center" gap={2.5} position="sticky" left={0} h="100%" px={3}
          bg="transparent" borderRadius="md" zIndex={1}
        >
          <ChevronIcon open={open} />
          <GroupIcon meta={meta} />
          <Text
            fontSize="2xs" fontWeight="700" letterSpacing="0.06em"
            textTransform="uppercase" noOfLines={1} color="redesign.heading"
          >
            {label}
          </Text>
          <Text fontSize="xs" color="redesign.text.muted">{lane.count}</Text>
          <GroupFigure group={lane} meta={meta} />
        </Flex>
      </Flex>

      {open && (
        <Flex gap={3} align="flex-start">
          {columns.map((column, columnIndex) => {
            const child = lane.cells.get(column.key);
            return (
              <Box key={column.key} minW={`${COLUMN_W}px`} maxW={`${COLUMN_W}px`} flex="none">
                {child ? (
                  <Group
                    {...cellProps}
                    group={child}
                    parent={column}
                    asCard
                    cell
                    prefetch={laneIndex < PREFETCH_LANES
                      && columnIndex < PREFETCH_LANE_COLUMNS}
                  />
                ) : (
                  <Box h="2px" />
                )}
              </Box>
            );
          })}
        </Flex>
      )}
    </Box>
  );
};

/** Column headers plus the bands under them, in one horizontal scroller so
 *  the two never drift apart. */
const SwimlaneBoard = ({ groups, swimlanes, cellProps, groupMeta }) => {
  const groupLabel = useGroupLabel();

  return (
    <Box overflowX="auto" py={3}>
      <Box minW="max-content">
        <Flex
          gap={3} pb={2} mb={3}
          borderBottom="1px solid" borderBottomColor="redesign.border"
        >
          {groups.map(group => (
            <Box key={group.key} minW={`${COLUMN_W}px`} maxW={`${COLUMN_W}px`} flex="none">
              <Flex align="center" gap={2} mb={1}>
                <GroupIcon meta={groupMeta?.(group)} />
                <Text
                  fontSize="2xs" fontWeight="700" letterSpacing="0.06em"
                  textTransform="uppercase" noOfLines={1} color="redesign.heading"
                >
                  {groupLabel(group)}
                </Text>
                <Text fontSize="xs" color="redesign.text.muted" flex="none">
                  {group.count}
                </Text>
              </Flex>
              <GroupFigure group={group} meta={groupMeta?.(group)} />
            </Box>
          ))}
        </Flex>

        {swimlanes.map((lane, i) => (
          <Swimlane
            key={lane.key}
            lane={lane}
            laneIndex={i}
            columns={groups}
            cellProps={cellProps}
            groupMeta={groupMeta}
          />
        ))}
      </Box>
    </Box>
  );
};

/**
 * How many groups get their first window fetched up front.
 *
 * Prefetching every group is what made an all-time range crawl: grouping by
 * category over a few years yields ~80 groups, each window ~100ms, all fired
 * at once against a browser that opens six connections. The user opens one or
 * two. This covers what fits on screen; anything below fetches on expand,
 * which is fast enough to feel immediate.
 */
const PREFETCH_GROUPS = 12;

/** Day groups render expanded, so every one on the page needs its rows; the
 *  page is sized for that in `groupPageFor`. */
const PREFETCH_GROUPS_DAY = 15;

const GroupedMovementsView = ({
  groups = [], swimlanes = [], loading = false, settings,
  filters = [], filterGroups = [],
  groupsOperator = 'and', startDate = null, endDate = null,
  onSelectMovement, renderRows, onRowsLoaded, resolveRows, groupMeta,
  hasMoreGroups = false, loadingMore = false, onLoadMoreGroups,
  // What to show when nothing matched. The page chooses (MI-315): it alone
  // knows whether a filter is narrowing the list or the workspace is empty.
  emptyContent,
}) => {
  const { t } = useTranslation('movements');
  const { grouping, ordering, rowsPerGroup, isVisible, view, hasSubgroup } = settings;

  // MI-318 — the "% of period" figure is gone. It was derived in the browser
  // from the groups *currently loaded*, so its denominator changed as you paged
  // and it silently mixed income against spend by taking magnitudes. Nothing on
  // screen explained any of that, and a percentage nobody can reproduce is worse
  // than no percentage.

  // Only the *first* load gets the spinner. Every group's open/closed state
  // lives inside its own component, so swapping the tree for a spinner on a
  // refresh — after a merge, say — throws that away and drops the user back at
  // a fully collapsed board with no idea where they were.
  if (loading && groups.length === 0) {
    return (
      <Flex justify="center" py={10}>
        <Spinner size="sm" color="redesign.text.muted" />
      </Flex>
    );
  }

  if (groups.length === 0) {
    return emptyContent ?? (
      <Text fontSize="sm" color="redesign.text.muted" textAlign="center" py={10}>
        {t('display.empty')}
      </Text>
    );
  }

  const common = (group, index) => ({
    key: group.key,
    group,
    parent: null,
    prefetch: index < (grouping === GROUPING_DAY ? PREFETCH_GROUPS_DAY : PREFETCH_GROUPS),
    ordering, rowsPerGroup, isVisible,
    filters, filterGroups, groupsOperator, startDate, endDate,
    onSelectMovement, renderRows, onRowsLoaded, resolveRows, groupMeta,
  });

  /** Refreshing over content the user is already reading: say so without
   *  taking it away. */
  const refreshing = loading && groups.length > 0;

  // MI-326 — the board asks for more the same way the list does. It sits
  // under the board, outside its sideways scroll, so it shows without
  // scrolling to the last column.
  const loadMoreGroups = hasMoreGroups && (
    <Box py={2}>
      <LoadMoreButton
        onClick={onLoadMoreGroups}
        isLoading={loadingMore}
        label={t('display.group.showMoreGroups')}
      />
    </Box>
  );

  if (view === VIEW_BOARD) {
    // With a subgroup the axes change rather than nesting: the group stays the
    // column and the subgroup becomes a band across all of them (rev 5).
    if (hasSubgroup && swimlanes.length > 0) {
      return (
        <Box opacity={refreshing ? 0.6 : 1} transition="opacity 0.15s">
        <SwimlaneBoard
          groups={groups}
          swimlanes={swimlanes}
          groupMeta={groupMeta}
          cellProps={{
            ordering, rowsPerGroup, isVisible,
            filters, filterGroups, groupsOperator, startDate, endDate,
            onSelectMovement, renderRows, onRowsLoaded, resolveRows,
          }}
        />
        {loadMoreGroups}
        </Box>
      );
    }
    return (
      <Box opacity={refreshing ? 0.6 : 1} transition="opacity 0.15s">
      <Flex gap={3} py={3} overflowX="auto" align="flex-start">
        {/* Columns arrive open — a board whose cards are all hidden behind a
            chevron shows nothing a list would not show better. Capped at the
            same budget as the prefetch: opening a column fetches its window,
            and 50 of those at once is the stampede revision 3 fixed. A column
            past the cap opens on click. Subgrouped boards take the swimlane
            path above, where the bands stay closed: a lane × column grid open
            on arrival is the "demasiada información" case. */}
        {groups.map((g, i) => (
          <Group {...common(g, i)} asCard defaultOpen={i < PREFETCH_GROUPS} />
        ))}
      </Flex>
      {loadMoreGroups}
      </Box>
    );
  }

  return (
    <Box opacity={refreshing ? 0.6 : 1} transition="opacity 0.15s">
      {groups.map((g, i) => <Group {...common(g, i)} />)}
      {loadMoreGroups}
    </Box>
  );
};

const NO_VERSIONS = {};

const GroupedMovements = ({ generation = 0, groupVersions = NO_VERSIONS, ...props }) => {
  const refresh = useMemo(
    () => ({ generation, versions: groupVersions }),
    [generation, groupVersions],
  );
  return (
    <GroupRefreshContext.Provider value={refresh}>
      <GroupedMovementsView {...props} />
    </GroupRefreshContext.Provider>
  );
};

export default GroupedMovements;
